import { parseCsvDiagnostics, normalizeCsvHeader, matchesAlias, type CsvDelimiter, type ParsedCsvDiagnostics } from "./csvNormalization";
import type { RideParserDiagnostics } from "../ride-center/services/rideUploadPipeline";
import type { Ride } from "../ride-center/types/ride";
import { parseTymewearTimeSeries, type TymewearTimeParseConfidence, type TymewearTimeParseMethod } from "./tymewearTime";

export const SCREENSHOT_ACCEPT = ".png,.jpg,.jpeg,.heic,.webp,image/*";

export type ActivitySourceType =
  | "Garmin FIT"
  | "TrainingPeaks FIT"
  | "TrainingPeaks FIT.gz"
  | "Tymewear CSV"
  | "Tymewear Dashboard Screenshot"
  | "CORE Screenshot"
  | "CORE export"
  | "Nix Biosensor export"
  | "Nix Screenshot"
  | "MacroFactor Workout"
  | "MacroFactor Nutrition"
  | "Epic Ride Weather"
  | "GPX"
  | "TCX"
  | "PDF"
  | "Athlete Notes"
  | "Unknown Screenshot"
  | "Unknown CSV"
  | "Unknown Companion Source";

export type ManualSourceLabel = "Tymewear" | "Nix" | "CORE" | "Weather" | "Nutrition" | "MacroFactor" | "Strength" | "Other" | "Other CSV";

export type ActivitySourceStatus =
  | "selected"
  | "queued"
  | "validating"
  | "parsing"
  | "analyzing"
  | "parsed"
  | "ready"
  | "needs confirmation"
  | "needs_reupload"
  | "unsupported"
  | "failed"
  | "error";
export type SourceConfidence = "high" | "medium" | "low";

export type TymewearInspection = {
  detectedDelimiter: CsvDelimiter;
  bomDetected: boolean;
  detectedColumns: string[];
  normalizedColumns: string[];
  matchedFields: string[];
  availableRespiratoryFields: string[];
  classificationScore: number;
  recordCount: number;
  timestampColumnCandidate: string | null;
  sampleIntervalSeconds: number | null;
  durationSeconds: number | null;
  alignmentMethod: "absolute_timestamps" | "elapsed_time_to_garmin_start" | "unavailable";
  alignmentConfidence: TymewearTimeParseConfidence;
  timeParseMethod: TymewearTimeParseMethod;
  firstTimestamp: string | null;
  lastTimestamp: string | null;
  overlapPercent: number | null;
  overlapDurationSeconds: number | null;
  respiratoryCoveragePercent: number | null;
  mappedFieldNames: Record<string, string>;
  fieldUnits: Record<string, string | null>;
  validNumericSampleCount: Record<string, number>;
  firstParsedRespiratoryRecords: Array<Record<string, string | number | null>>;
  warnings: string[];
  rejectionReasons: string[];
  timeColumnFirstValues: Array<string | null>;
  timeColumnFirstValueTypes: TymewearTimeParseMethod[];
  timeColumnValidCount: number;
  timeColumnDuplicateCount: number;
  timeColumnDecreasingCount: number;
  timeColumnMedianDeltaSeconds: number | null;
  timeColumnMonotonicNonDecreasing: boolean;
};

export type FitClassificationDiagnostics = {
  provider: "Garmin" | "TrainingPeaks" | "Unknown FIT provider";
  format: "FIT" | "FIT.gz";
  telemetryStreams: string[];
  recordCount: number;
  hasValidSport: boolean;
  hasValidStartTime: boolean;
  hasValidDuration: boolean;
  validationWarnings: string[];
  evidence: string[];
  confidenceScore: number;
  confidenceReasons: string[];
};

export type CsvClassificationDiagnostics = {
  detectedDelimiter: CsvDelimiter;
  bomDetected: boolean;
  totalRows: number;
  rawFirstLines: string[];
  columnsWithNumericValues: string[];
  candidateTimestampColumns: string[];
  candidateRespiratoryColumns: string[];
  firstParsedRecords: Array<Record<string, string | null>>;
  normalizedHeaders: string[];
  matchedTymewearFields: string[];
  classificationScoresBySource: Record<"tymewear" | "nix" | "weather" | "nutrition" | "strength" | "athlete_notes", number>;
  classificationScore: number;
  detectionReasons: string[];
  failedReasons: string[];
  finalSourceType: ActivitySourceType;
  parserInvoked: boolean;
};

export type ConfirmedScreenshotValue = {
  field: string;
  value: string | number;
  unit?: string;
  confirmedAt: string;
};

export type ActivitySource = {
  id: string;
  file: File;
  fingerprint: string;
  role: "primary" | "companion" | "screenshot";
  type: ActivitySourceType;
  status: ActivitySourceStatus;
  confidence: SourceConfidence;
  note: string | null;
  tymewear: TymewearInspection | null;
  csvClassification: CsvClassificationDiagnostics | null;
  fitDiagnostics?: FitClassificationDiagnostics | null;
  previewUrl: string | null;
  confirmedScreenshotValues: ConfirmedScreenshotValue[];
};

export type ActivityMetadata = {
  startTime: number | null;
  durationSeconds: number | null;
  distanceMeters: number | null;
};

export type ActivitySession = {
  id: string;
  primary: ActivitySource | null;
  supportingRideSources: ActivitySource[];
  companionSources: ActivitySource[];
  activityMetadata: ActivityMetadata | null;
  sessionOnly: true;
};

export type ActivityMatchResult = {
  confidence: SourceConfidence;
  score: number;
  reasons: string[];
};

export type ActivityCompletenessItem = {
  label: string;
  present: boolean;
};

export type ActivityCompleteness = {
  items: ActivityCompletenessItem[];
  completenessPercent: number;
};

export type RideSourceSummaryItem = {
  label: string;
  status: "Detected" | "Missing" | "Not Applicable";
};

export function createActivitySession(): ActivitySession {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    primary: null,
    supportingRideSources: [],
    companionSources: [],
    activityMetadata: null,
    sessionOnly: true
  };
}

const SUPPORTED_COMPANION_EXTENSIONS = [".csv", ".xlsx", ".xls", ".json", ".txt", ".gpx", ".tcx", ".fit", ".fit.gz", ".png", ".jpg", ".jpeg", ".heic", ".webp", ".pdf"];
const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".heic", ".webp"];

function hasExtension(name: string, extensions: string[]): boolean {
  const normalized = name.toLowerCase();
  return extensions.some(extension => normalized.endsWith(extension));
}

function isFitFilename(name: string): boolean {
  const normalized = name.toLowerCase();
  return normalized.endsWith(".fit") || normalized.endsWith(".fit.gz");
}

function filenameIncludes(file: File, values: string[]): boolean {
  const name = file.name.toLowerCase();
  return values.some(value => name.includes(value));
}

export function getSourceFingerprint(file: File): string {
  return `${file.name.toLowerCase()}::${file.size}::${file.lastModified}`;
}

export function isDuplicateSource(file: File, sources: ActivitySource[]): ActivitySource | null {
  const exactFingerprint = getSourceFingerprint(file);
  return sources.find(source => source.fingerprint === exactFingerprint)
    ?? sources.find(source => source.file.name.toLowerCase() === file.name.toLowerCase() && source.file.size === file.size)
    ?? null;
}

const TYMEWEAR_TIME_ALIASES = ["timestamp", "date time", "datetime"];
const TYMEWEAR_ELAPSED_TIME_ALIASES = ["elapsed time", "seconds", "sample time"];
const TYMEWEAR_MINUTE_VENTILATION_ALIASES = ["minute ventilation", "ventilation", "ve", "ve l min", "ve l/min", "ventilation l min", "ventilation l/min"];
const TYMEWEAR_BREATHING_RATE_ALIASES = ["breathing frequency", "breathing rate", "respiratory rate", "respiration rate", "br", "bf", "breaths per minute", "breaths/min", "rpm", "rr", "rf"];
const TYMEWEAR_TIDAL_VOLUME_ALIASES = ["tidal volume", "vt", "tv", "tidal volume ml", "tidal volume l"];
const TYMEWEAR_SECONDARY_ALIASES = [
  "inspiratory time",
  "expiratory time",
  "respiratory duty cycle",
  "inhale exhale ratio",
  "respiratory intensity",
  "breathing efficiency",
  "ventilation threshold",
  "vt1",
  "vt2",
  "vo2 threshold",
  "respiratory threshold",
  "heart rate",
  "hr",
  "rpe",
  "segment",
  "zone",
  "device threshold",
  "threshold"
];

const TYMEWEAR_FIELD_ALIAS_MAP: Record<string, string[]> = {
  timestamp: TYMEWEAR_TIME_ALIASES,
  elapsedSeconds: TYMEWEAR_ELAPSED_TIME_ALIASES,
  minuteVentilation: TYMEWEAR_MINUTE_VENTILATION_ALIASES,
  breathingFrequency: TYMEWEAR_BREATHING_RATE_ALIASES,
  tidalVolume: TYMEWEAR_TIDAL_VOLUME_ALIASES,
  respiratoryRate: ["respiratory rate", "respiration rate", "rr"],
  deviceThreshold: ["device threshold", "threshold", "zone"],
  heartRate: ["heart rate", "hr", "heartrate"],
  rpe: ["rpe", "rate of perceived exertion"],
  inspiratoryTime: ["inspiratory time"],
  expiratoryTime: ["expiratory time"],
  respiratoryDutyCycle: ["respiratory duty cycle"],
  inhaleExhaleRatio: ["inhale exhale ratio", "i e ratio", "ie ratio"],
  ventilationThreshold: ["ventilation threshold", "respiratory threshold", "vo2 threshold"],
  vt1: ["vt1"],
  vt2: ["vt2"],
  qualityFlag: ["quality flag", "quality", "validity", "status", "signal quality"],
  segmentLabel: ["segment", "segment label", "lap", "block"]
};

function extractUnitFromHeader(header: string): string | null {
  const match = header.match(/[\[(]([^\])]+)[\])]/);
  return match?.[1]?.trim() || null;
}

type TymewearCsvSignature = {
  matchedFields: string[];
  score: number;
  hasTimestamp: boolean;
  hasPrimaryRespiratoryField: boolean;
  respiratorySignals: string[];
  failedReasons: string[];
};

function inspectTymewearSignature(parsedCsv: ParsedCsvDiagnostics): TymewearCsvSignature {
  const normalizedHeaders = parsedCsv.normalizedHeaders.map(normalizeCsvHeader);
  const hasAnyAlias = (aliases: string[]) => normalizedHeaders.some(header => aliases.some(alias => matchesAlias(header, alias)));

  const hasTimestamp = hasAnyAlias(TYMEWEAR_TIME_ALIASES) || normalizedHeaders.includes("time") || parsedCsv.candidateTimestampColumns.length > 0;
  const hasElapsed = hasAnyAlias(TYMEWEAR_ELAPSED_TIME_ALIASES);
  const hasMinuteVentilation = hasAnyAlias(TYMEWEAR_MINUTE_VENTILATION_ALIASES);
  const hasBreathingFrequency = hasAnyAlias(TYMEWEAR_BREATHING_RATE_ALIASES);
  const hasTidalVolume = hasAnyAlias(TYMEWEAR_TIDAL_VOLUME_ALIASES);
  const hasSecondaryRespiratory = hasAnyAlias(TYMEWEAR_SECONDARY_ALIASES);
  const hasSeriesDepth = parsedCsv.totalRows >= 20;
  const hasNumericSeries = parsedCsv.columnsWithNumericValues.length >= 2;

  const matchedFields: string[] = [];
  if (hasTimestamp) matchedFields.push("timestamp");
  if (hasElapsed) matchedFields.push("elapsed_time");
  if (hasMinuteVentilation) matchedFields.push("minute_ventilation");
  if (hasBreathingFrequency) matchedFields.push("breathing_frequency");
  if (hasTidalVolume) matchedFields.push("tidal_volume");
  if (hasSecondaryRespiratory) matchedFields.push("secondary_respiratory");
  if (hasSeriesDepth) matchedFields.push("time_series_depth");
  if (hasNumericSeries) matchedFields.push("numeric_series");

  const respiratorySignals = [
    hasMinuteVentilation ? "minute_ventilation" : null,
    hasBreathingFrequency ? "breathing_frequency" : null,
    hasTidalVolume ? "tidal_volume" : null,
    hasSecondaryRespiratory ? "secondary_respiratory" : null
  ].filter((value): value is string => value !== null);

  const failedReasons: string[] = [];
  if (!hasTimestamp && !hasElapsed) failedReasons.push("No timestamp or elapsed-time column could be confirmed.");
  if (!hasMinuteVentilation) failedReasons.push("Minute ventilation column was not detected.");
  if (!hasBreathingFrequency && !hasTidalVolume) failedReasons.push("Neither breathing-frequency nor tidal-volume column was detected.");
  if (!hasNumericSeries) failedReasons.push("CSV did not include enough numeric series columns.");
  if (!hasSeriesDepth) failedReasons.push("CSV did not include enough rows for a respiratory time series.");

  const score =
    (hasTimestamp ? 2 : 0)
    + (hasElapsed ? 2 : 0)
    + (hasMinuteVentilation ? 3 : 0)
    + (hasBreathingFrequency ? 3 : 0)
    + (hasTidalVolume ? 2 : 0)
    + (hasSecondaryRespiratory ? 1 : 0)
    + (hasNumericSeries ? 1 : 0)
    + (hasSeriesDepth ? 1 : 0);

  return {
    matchedFields,
    score,
    hasTimestamp: hasTimestamp || hasElapsed,
    hasPrimaryRespiratoryField: hasMinuteVentilation && (hasBreathingFrequency || hasTidalVolume),
    respiratorySignals,
    failedReasons
  };
}

function toFiniteNumber(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Number(value.trim().replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function toTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value.trim());
  return Number.isNaN(parsed) ? null : parsed;
}

function findColumnIndex(headers: string[], aliases: string[]): number {
  return headers.findIndex(header => aliases.some(alias => matchesAlias(header, alias)));
}

export function inspectTymewearCsv(text: string, primaryRange?: { start: number; end: number }, parsedCsv?: ParsedCsvDiagnostics): TymewearInspection {
  const csv = parsedCsv ?? parseCsvDiagnostics(text);
  const normalizedColumns = csv.normalizedHeaders;
  const signature = inspectTymewearSignature(csv);

  const fieldIndexes = Object.entries(TYMEWEAR_FIELD_ALIAS_MAP).reduce<Record<string, number>>((accumulator, [field, aliases]) => {
    const index = findColumnIndex(normalizedColumns, aliases);
    if (index >= 0) accumulator[field] = index;
    return accumulator;
  }, {});

  const mappedFieldNames = Object.entries(fieldIndexes).reduce<Record<string, string>>((accumulator, [field, index]) => {
    accumulator[field] = csv.headerRowRaw[index] ?? normalizedColumns[index] ?? field;
    return accumulator;
  }, {});

  const fieldUnits = Object.entries(fieldIndexes).reduce<Record<string, string | null>>((accumulator, [field, index]) => {
    accumulator[field] = extractUnitFromHeader(csv.headerRowRaw[index] ?? "");
    return accumulator;
  }, {});

  const validNumericSampleCount = Object.entries(fieldIndexes).reduce<Record<string, number>>((accumulator, [field, index]) => {
    const total = csv.rows.reduce((sum, row) => {
      const value = toFiniteNumber(row[index]);
      if (value === null) return sum;
      if (["minuteVentilation", "breathingFrequency", "tidalVolume", "respiratoryRate"].includes(field) && value < 0) return sum;
      return sum + 1;
    }, 0);
    accumulator[field] = total;
    return accumulator;
  }, {});

  const firstParsedRespiratoryRecords = csv.rows.slice(0, 3).map(row => ({
    timestamp: row[fieldIndexes.timestamp] ?? null,
    elapsedSeconds: toFiniteNumber(row[fieldIndexes.elapsedSeconds]),
    minuteVentilation: toFiniteNumber(row[fieldIndexes.minuteVentilation]),
    breathingFrequency: toFiniteNumber(row[fieldIndexes.breathingFrequency]),
    tidalVolume: toFiniteNumber(row[fieldIndexes.tidalVolume])
  }));

  const timestampColumnIndex = findColumnIndex(normalizedColumns, TYMEWEAR_TIME_ALIASES);
  const fallbackTimeColumnIndex = normalizedColumns.findIndex(column => column === "time");
  const elapsedColumnIndex = findColumnIndex(normalizedColumns, TYMEWEAR_ELAPSED_TIME_ALIASES);
  const timeColumnIndex = timestampColumnIndex >= 0
    ? timestampColumnIndex
    : elapsedColumnIndex >= 0
      ? elapsedColumnIndex
      : fallbackTimeColumnIndex;

  const rawTimeValues = timeColumnIndex >= 0
    ? csv.rows.map(row => row[timeColumnIndex] || null)
    : [];
  const parsedTimeSeries = parseTymewearTimeSeries(rawTimeValues);

  let timestamps = parsedTimeSeries.points
    .map(point => point.absoluteTimestampMs)
    .filter((value): value is number => value !== null);
  let alignmentMethod: TymewearInspection["alignmentMethod"] = timestamps.length > 0 ? "absolute_timestamps" : "unavailable";
  if (!timestamps.length && parsedTimeSeries.firstValidElapsedSeconds !== null && primaryRange) {
    timestamps = parsedTimeSeries.points
      .map(point => point.elapsedSeconds === null ? null : primaryRange.start + (point.elapsedSeconds - parsedTimeSeries.firstValidElapsedSeconds!) * 1000)
      .filter((value): value is number => value !== null);
    if (timestamps.length > 0) alignmentMethod = "elapsed_time_to_garmin_start";
  }

  if (!timestamps.length && parsedTimeSeries.firstValidElapsedSeconds !== null) {
    const metadataDateToken = csv.rawFirstLines
      .map(line => line.match(/(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?Z?)/i)?.[1] ?? null)
      .find((value): value is string => value !== null);
    const metadataStart = metadataDateToken ? Date.parse(metadataDateToken) : Number.NaN;
    if (Number.isFinite(metadataStart)) {
      timestamps = parsedTimeSeries.points
        .map(point => point.elapsedSeconds === null ? null : metadataStart + (point.elapsedSeconds - parsedTimeSeries.firstValidElapsedSeconds!) * 1000)
        .filter((value): value is number => value !== null);
      if (timestamps.length > 0) alignmentMethod = "absolute_timestamps";
    }
  }

  const firstTimestampValue = timestamps.length ? Math.min(...timestamps) : null;
  const lastTimestampValue = timestamps.length ? Math.max(...timestamps) : null;
  const sampleIntervalSeconds = parsedTimeSeries.sampleIntervalSeconds;

  const durationSeconds = parsedTimeSeries.durationSeconds ?? (firstTimestampValue !== null && lastTimestampValue !== null && lastTimestampValue > firstTimestampValue
    ? Math.round((lastTimestampValue - firstTimestampValue) / 1000)
    : null);

  let overlapPercent: number | null = null;
  let overlapDurationSeconds: number | null = null;
  if (primaryRange && firstTimestampValue !== null && lastTimestampValue !== null && lastTimestampValue > firstTimestampValue) {
    const overlap = Math.max(0, Math.min(primaryRange.end, lastTimestampValue) - Math.max(primaryRange.start, firstTimestampValue));
    overlapDurationSeconds = Math.round(overlap / 1000);
    overlapPercent = Math.round((overlap / (lastTimestampValue - firstTimestampValue)) * 100);
  }

  const respiratoryFieldKeys = ["minuteVentilation", "breathingFrequency", "tidalVolume", "respiratoryRate"];
  const maxRespiratorySamples = Math.max(0, ...respiratoryFieldKeys.map(key => validNumericSampleCount[key] ?? 0));
  const respiratoryCoveragePercent = csv.totalRows > 0
    ? Math.round((maxRespiratorySamples / csv.totalRows) * 100)
    : null;
  const warnings: string[] = [];
  if (alignmentMethod === "unavailable") warnings.push("No usable alignment column was confirmed.");
  if (respiratoryCoveragePercent !== null && respiratoryCoveragePercent < 30) warnings.push("Respiratory numeric coverage is below 30%.");
  if (sampleIntervalSeconds !== null && sampleIntervalSeconds > 10) warnings.push("Sample cadence appears sparse.");
  if (!parsedTimeSeries.stats.monotonicNonDecreasing) warnings.push("Time column is not monotonic non-decreasing.");

  const rejectionReasons = [...signature.failedReasons];
  if (primaryRange && overlapPercent !== null && overlapPercent < 30) {
    rejectionReasons.push("Overlap with primary ride is below 30%.");
  }

  return {
    detectedDelimiter: csv.detectedDelimiter,
    bomDetected: csv.bomDetected,
    detectedColumns: csv.headerRowRaw,
    normalizedColumns,
    matchedFields: signature.matchedFields,
    availableRespiratoryFields: signature.respiratorySignals,
    classificationScore: signature.score,
    recordCount: csv.totalRows,
    timestampColumnCandidate: timeColumnIndex >= 0 ? csv.headerRowRaw[timeColumnIndex] : null,
    sampleIntervalSeconds,
    durationSeconds,
    alignmentMethod,
    alignmentConfidence: parsedTimeSeries.parseConfidence,
    timeParseMethod: parsedTimeSeries.stats.detectedFormat,
    firstTimestamp: firstTimestampValue === null ? null : new Date(firstTimestampValue).toISOString(),
    lastTimestamp: lastTimestampValue === null ? null : new Date(lastTimestampValue).toISOString(),
    overlapPercent,
    overlapDurationSeconds,
    respiratoryCoveragePercent,
    mappedFieldNames,
    fieldUnits,
    validNumericSampleCount,
    firstParsedRespiratoryRecords,
    warnings,
    rejectionReasons,
    timeColumnFirstValues: parsedTimeSeries.stats.firstTenRawValues,
    timeColumnFirstValueTypes: parsedTimeSeries.stats.firstTenParsedTypes,
    timeColumnValidCount: parsedTimeSeries.stats.validValueCount,
    timeColumnDuplicateCount: parsedTimeSeries.stats.duplicateValueCount,
    timeColumnDecreasingCount: parsedTimeSeries.stats.decreasingValueCount,
    timeColumnMedianDeltaSeconds: parsedTimeSeries.stats.medianDeltaSeconds,
    timeColumnMonotonicNonDecreasing: parsedTimeSeries.stats.monotonicNonDecreasing
  };
}

function classifyTymewearMatch(
  inspection: TymewearInspection,
  primaryRange?: { start: number; end: number }
): { confidence: SourceConfidence; reasons: string[] } {
  if (!primaryRange) {
    return { confidence: "medium", reasons: ["ride alignment pending"] };
  }

  const firstTimestamp = inspection.firstTimestamp ? Date.parse(inspection.firstTimestamp) : Number.NaN;
  const lastTimestamp = inspection.lastTimestamp ? Date.parse(inspection.lastTimestamp) : Number.NaN;
  if (!Number.isFinite(firstTimestamp) || !Number.isFinite(lastTimestamp) || lastTimestamp <= firstTimestamp) {
    return { confidence: "low", reasons: ["timestamps were not usable"] };
  }

  const primaryDurationMs = Math.max(0, primaryRange.end - primaryRange.start);
  const sourceDurationMs = lastTimestamp - firstTimestamp;
  const startDeltaMinutes = Math.abs(firstTimestamp - primaryRange.start) / 60_000;
  const durationDifference = primaryDurationMs > 0 ? Math.abs(primaryDurationMs - sourceDurationMs) / primaryDurationMs : null;
  const overlapPercent = inspection.overlapPercent ?? 0;

  let score = 0;
  const reasons: string[] = [];
  if (startDeltaMinutes <= 10) {
    score += 0.4;
    reasons.push("start time within 10 minutes");
  } else if (startDeltaMinutes <= 20) {
    score += 0.2;
    reasons.push("start time within 20 minutes");
  }

  if (durationDifference !== null) {
    if (durationDifference <= 0.1) {
      score += 0.3;
      reasons.push("duration within 10%");
    } else if (durationDifference <= 0.2) {
      score += 0.15;
      reasons.push("duration within 20%");
    }
  }

  if (overlapPercent >= 70) {
    score += 0.3;
    reasons.push("ride overlap at least 70%");
  } else if (overlapPercent >= 50) {
    score += 0.2;
    reasons.push("ride overlap at least 50%");
  } else if (overlapPercent >= 30) {
    score += 0.1;
    reasons.push("ride overlap at least 30%");
  }

  if (score >= 0.8 && overlapPercent >= 50) {
    return { confidence: "high", reasons };
  }
  if (score >= 0.5) {
    return { confidence: "medium", reasons };
  }
  return { confidence: "low", reasons: reasons.length ? reasons : ["did not align with primary ride"] };
}

function inferFitProviderFromText(rawData: unknown): string[] {
  const matches: string[] = [];
  const inspect = (value: unknown): void => {
    if (typeof value === "string") {
      const normalized = value.toLowerCase();
      if (normalized.includes("trainingpeaks")) matches.push("raw_data_trainingpeaks_marker");
      if (normalized.includes("garmin")) matches.push("raw_data_garmin_marker");
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(inspect);
      return;
    }
    if (value && typeof value === "object") {
      Object.values(value as Record<string, unknown>).forEach(inspect);
    }
  };
  inspect(rawData);
  return [...new Set(matches)];
}

function fitFormat(file: File): "FIT" | "FIT.gz" {
  return file.name.toLowerCase().endsWith(".fit.gz") ? "FIT.gz" : "FIT";
}

function detectFitProvider(file: File, ride?: Ride): { provider: FitClassificationDiagnostics["provider"]; evidence: string[] } {
  const lowerName = file.name.toLowerCase();
  const evidence: string[] = [];
  if (lowerName.includes("trainingpeaks") || lowerName.includes("training-peaks") || lowerName.includes("tp")) {
    evidence.push("filename_trainingpeaks_hint");
  }
  if (lowerName.includes("garmin")) {
    evidence.push("filename_garmin_hint");
  }
  if (ride?.rawData) {
    evidence.push(...inferFitProviderFromText(ride.rawData));
  }

  if (evidence.some(item => item.includes("trainingpeaks"))) {
    return { provider: "TrainingPeaks", evidence };
  }
  if (evidence.some(item => item.includes("garmin"))) {
    return { provider: "Garmin", evidence };
  }
  return { provider: "Unknown FIT provider", evidence };
}

function fitTelemetryStreams(ride: Ride): string[] {
  const has = (selector: (record: Ride["records"][number]) => unknown) => ride.records.some(record => selector(record) !== null && selector(record) !== undefined);
  const streams: string[] = [];
  if (has(record => record.powerWatts)) streams.push("power");
  if (has(record => record.heartRateBpm)) streams.push("heart_rate");
  if (has(record => record.cadenceRpm)) streams.push("cadence");
  if (has(record => record.latitudeDegrees) && has(record => record.longitudeDegrees)) streams.push("gps");
  if (has(record => record.altitudeMeters)) streams.push("altitude");
  if (has(record => record.temperatureCelsius)) streams.push("temperature");
  return streams;
}

export function classifyParsedFitSource(file: File, ride: Ride, diagnostics: RideParserDiagnostics): {
  sourceType: ActivitySourceType;
  confidence: SourceConfidence;
  note: string;
  fitDiagnostics: FitClassificationDiagnostics;
} {
  const { provider, evidence: providerEvidence } = detectFitProvider(file, ride);
  const format = fitFormat(file);
  const streams = fitTelemetryStreams(ride);
  const warnings = diagnostics.warnings ?? [];
  const hasValidSport = diagnostics.detectedSport.toLowerCase() !== "unknown";
  const hasValidStartTime = ride.rideDate !== null;
  const hasValidDuration = (ride.elapsedTime ?? ride.movingTime ?? 0) > 0;

  let score = 0;
  const reasons: string[] = [];
  if (hasValidSport) {
    score += 0.15;
    reasons.push("valid sport/activity detected");
  }
  if (hasValidStartTime) {
    score += 0.15;
    reasons.push("valid start timestamp detected");
  }
  if (hasValidDuration) {
    score += 0.15;
    reasons.push("valid ride duration detected");
  }
  if (ride.records.length >= 1000) {
    score += 0.2;
    reasons.push("sufficient FIT record count");
  } else if (ride.records.length >= 250) {
    score += 0.1;
    reasons.push("moderate FIT record count");
  }
  if (streams.length >= 3) {
    score += 0.25;
    reasons.push("3+ major telemetry streams available");
  } else if (streams.length >= 2) {
    score += 0.15;
    reasons.push("2 major telemetry streams available");
  }
  if (warnings.length === 0) {
    score += 0.1;
    reasons.push("no parser validation warnings");
  } else if (warnings.length <= 2) {
    score += 0.05;
    reasons.push("limited parser validation warnings");
  }

  const confidence: SourceConfidence = score >= 0.75
    ? "high"
    : score >= 0.5
      ? "medium"
      : "low";

  const sourceType: ActivitySourceType = provider === "TrainingPeaks"
    ? (format === "FIT.gz" ? "TrainingPeaks FIT.gz" : "TrainingPeaks FIT")
    : "Garmin FIT";

  const note = `${diagnostics.detectedSport} · ${ride.records.length.toLocaleString()} records`;

  return {
    sourceType,
    confidence,
    note,
    fitDiagnostics: {
      provider,
      format,
      telemetryStreams: streams,
      recordCount: ride.records.length,
      hasValidSport,
      hasValidStartTime,
      hasValidDuration,
      validationWarnings: warnings,
      evidence: providerEvidence,
      confidenceScore: Math.round(score * 100),
      confidenceReasons: reasons
    }
  };
}

function csvScoreByPattern(headers: string[], aliases: string[]): number {
  const matches = headers.filter(header => aliases.some(alias => matchesAlias(header, alias))).length;
  return matches;
}

export async function classifyActivitySource(
  file: File,
  role: ActivitySource["role"],
  primaryRange?: { start: number; end: number },
  options?: { manualLabel?: ManualSourceLabel }
): Promise<ActivitySource> {
  const lowerName = file.name.toLowerCase();
  const isImage = file.type.startsWith("image/") || hasExtension(lowerName, IMAGE_EXTENSIONS);
  const supported = role === "primary" ? isFitFilename(file.name) : hasExtension(lowerName, SUPPORTED_COMPANION_EXTENSIONS) || isImage;
  let type: ActivitySourceType = "Unknown Companion Source";
  let confidence: SourceConfidence = "low";
  let status: ActivitySourceStatus = supported ? "ready" : "unsupported";
  let note: string | null = null;
  let tymewear: TymewearInspection | null = null;
  let csvClassification: CsvClassificationDiagnostics | null = null;
  let fitDiagnostics: FitClassificationDiagnostics | null = null;

  if (isFitFilename(file.name)) {
    const provider = detectFitProvider(file);
    type = provider.provider === "TrainingPeaks"
      ? (fitFormat(file) === "FIT.gz" ? "TrainingPeaks FIT.gz" : "TrainingPeaks FIT")
      : "Garmin FIT";
    confidence = provider.provider === "Unknown FIT provider" ? "medium" : "high";
    fitDiagnostics = {
      provider: provider.provider,
      format: fitFormat(file),
      telemetryStreams: [],
      recordCount: 0,
      hasValidSport: false,
      hasValidStartTime: false,
      hasValidDuration: false,
      validationWarnings: [],
      evidence: provider.evidence,
      confidenceScore: confidence === "high" ? 80 : 55,
      confidenceReasons: provider.provider === "Unknown FIT provider" ? ["provider unresolved before parse"] : ["provider inferred from filename/raw hints"]
    };
  } else if (role === "primary") {
    note = "Primary ride must be a .fit or .fit.gz file.";
  } else if (isImage) {
    status = "needs confirmation";
    if (filenameIncludes(file, ["tymewear", "tyme", "respirat", "ventilation"])) {
      type = "Tymewear Dashboard Screenshot";
      confidence = "medium";
      note = "Respiratory data detected · Pending extraction";
    } else if (filenameIncludes(file, ["nix", "hydration", "sweat"])) {
      type = "Nix Screenshot";
      confidence = "medium";
      note = "Sweat analysis detected · Pending OCR";
    } else if (filenameIncludes(file, ["epic", "weather", "forecast", "wind"])) {
      type = "Epic Ride Weather";
      confidence = "medium";
    } else {
      type = "Unknown Screenshot";
      note = "Screenshot values require athlete confirmation.";
    }
  } else if (/\.gpx$/i.test(file.name)) {
    type = "GPX";
    confidence = "high";
  } else if (/\.tcx$/i.test(file.name)) {
    type = "TCX";
    confidence = "high";
  } else if (/\.xlsx?$/.test(lowerName) && filenameIncludes(file, ["macrofactor", "macro-factor"])) {
    type = filenameIncludes(file, ["nutrition", "food", "intake"]) ? "MacroFactor Nutrition" : "MacroFactor Workout";
    confidence = "high";
    note = type === "MacroFactor Nutrition" ? "Nutrition log · Ready" : "Workout export · Ready";
  } else if (/\.pdf$/i.test(file.name)) {
    type = "PDF";
    confidence = "medium";
  } else if (/\.csv$|\.json$|\.txt$/i.test(file.name)) {
    const text = await file.text();
    const parsedCsv = parseCsvDiagnostics(text);
    const normalizedHeaders = parsedCsv.normalizedHeaders;
    const header = normalizedHeaders.join(" ");
    const signature = inspectTymewearSignature(parsedCsv);
    const manualTymewear = options?.manualLabel === "Tymewear";
    const tymewearDetected = manualTymewear || (signature.hasTimestamp && signature.hasPrimaryRespiratoryField && signature.score >= 7);

    const nixScore = csvScoreByPattern(normalizedHeaders, ["sodium", "sweat rate", "fluid loss", "nix", "hydration"]);
    const weatherScore = csvScoreByPattern(normalizedHeaders, ["temperature", "wind speed", "precipitation", "forecast", "weather"]);
    const nutritionScore = csvScoreByPattern(normalizedHeaders, ["nutrition", "carb", "calorie", "hydration", "intake"]);
    const strengthScore = csvScoreByPattern(normalizedHeaders, ["workout", "interval", "target power", "set", "repetition"]);

    const classificationScoresBySource: CsvClassificationDiagnostics["classificationScoresBySource"] = {
      tymewear: signature.score,
      nix: nixScore,
      weather: weatherScore,
      nutrition: nutritionScore,
      strength: strengthScore,
      athlete_notes: Math.max(0, 3 - signature.score)
    };
    const detectionReasons = [...signature.matchedFields];
    if (manualTymewear) detectionReasons.push("manual_label_tymewear");

    if (tymewearDetected) {
      type = "Tymewear CSV";
      tymewear = inspectTymewearCsv(text, primaryRange, parsedCsv);
      const respiratoryFieldCount = signature.matchedFields.filter(field => ["minute_ventilation", "breathing_frequency", "tidal_volume"].includes(field)).length;
      const match = classifyTymewearMatch(tymewear, primaryRange);
      confidence = match.confidence === "high"
        ? "high"
        : respiratoryFieldCount >= 2
          ? "medium"
          : "low";
      status = confidence === "low" ? "needs confirmation" : "parsed";
      if (primaryRange && tymewear.overlapPercent !== null) {
        note = `Aligned · ${tymewear.overlapPercent}% ride overlap · ${match.reasons.join(" · ") || "alignment evaluated"}`;
      } else {
        note = `${tymewear.recordCount.toLocaleString()} records · alignment pending`;
      }
      if (manualTymewear && signature.failedReasons.length) {
        note = `${note} · Missing fields: ${signature.failedReasons.join(" ")}`;
      }
      csvClassification = {
        detectedDelimiter: parsedCsv.detectedDelimiter,
        bomDetected: parsedCsv.bomDetected,
        totalRows: parsedCsv.totalRows,
        rawFirstLines: parsedCsv.rawFirstLines,
        columnsWithNumericValues: parsedCsv.columnsWithNumericValues,
        candidateTimestampColumns: parsedCsv.candidateTimestampColumns,
        candidateRespiratoryColumns: parsedCsv.candidateRespiratoryColumns,
        firstParsedRecords: parsedCsv.firstParsedRecords,
        normalizedHeaders: tymewear.normalizedColumns,
        matchedTymewearFields: signature.matchedFields,
        classificationScoresBySource,
        classificationScore: signature.score,
        detectionReasons,
        failedReasons: signature.failedReasons,
        finalSourceType: type,
        parserInvoked: true
      };
    } else if (filenameIncludes(file, ["core"]) || /core.?temp|skin.?temp/.test(header)) {
      type = "CORE export";
      confidence = "medium";
    } else if (filenameIncludes(file, ["nix"]) || /sodium|sweat.?rate|fluid.?loss/.test(header)) {
      type = "Nix Biosensor export";
      confidence = "medium";
    } else if (filenameIncludes(file, ["epic", "weather"]) || /temperature|wind.?speed|precipitation|forecast/.test(header)) {
      type = "Epic Ride Weather";
      confidence = "medium";
    } else if (/workout|interval|target.?power/.test(header)) {
      type = filenameIncludes(file, ["macrofactor", "macro-factor"]) ? "MacroFactor Workout" : "Athlete Notes";
      confidence = "medium";
    } else if (/nutrition|carb|calorie|hydration/.test(header)) {
      type = filenameIncludes(file, ["macrofactor", "macro-factor"]) ? "MacroFactor Nutrition" : "Athlete Notes";
      confidence = "medium";
    } else {
      type = /\.csv$/i.test(file.name) ? "Unknown CSV" : "Athlete Notes";
      status = "needs confirmation";
    }

    if (!csvClassification && /\.csv$/i.test(file.name)) {
      csvClassification = {
        detectedDelimiter: parsedCsv.detectedDelimiter,
        bomDetected: parsedCsv.bomDetected,
        totalRows: parsedCsv.totalRows,
        rawFirstLines: parsedCsv.rawFirstLines,
        columnsWithNumericValues: parsedCsv.columnsWithNumericValues,
        candidateTimestampColumns: parsedCsv.candidateTimestampColumns,
        candidateRespiratoryColumns: parsedCsv.candidateRespiratoryColumns,
        firstParsedRecords: parsedCsv.firstParsedRecords,
        normalizedHeaders,
        matchedTymewearFields: signature.matchedFields,
        classificationScoresBySource,
        classificationScore: signature.score,
        detectionReasons,
        failedReasons: signature.failedReasons,
        finalSourceType: type,
        parserInvoked: false
      };
    }
  } else if (/\.xlsx?$/.test(lowerName)) {
    status = "needs confirmation";
  }

  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    file,
    fingerprint: getSourceFingerprint(file),
    role,
    type,
    status,
    confidence,
    note,
    tymewear,
    csvClassification,
    fitDiagnostics,
    previewUrl: isImage && typeof URL !== "undefined" && typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : null,
    confirmedScreenshotValues: []
  };
}

function relativeDifference(first: number | null, second: number | null): number | null {
  if (first === null || second === null || Math.max(first, second) === 0) return null;
  return Math.abs(first - second) / Math.max(first, second);
}

export function matchActivityMetadata(existing: ActivityMetadata, candidate: ActivityMetadata, candidateFile?: File): ActivityMatchResult {
  let score = 0;
  let availableWeight = 0;
  const reasons: string[] = [];
  if (existing.startTime !== null && candidate.startTime !== null) {
    availableWeight += 0.5;
    const startDeltaMinutes = Math.abs(existing.startTime - candidate.startTime) / 60_000;
    if (startDeltaMinutes <= 10) {
      score += 0.5;
      reasons.push("start time within 10 minutes");
    }
  }
  const durationDifference = relativeDifference(existing.durationSeconds, candidate.durationSeconds);
  if (durationDifference !== null) {
    availableWeight += 0.25;
    if (durationDifference <= 0.1) {
      score += 0.25;
      reasons.push("duration within 10%");
    }
  }
  const distanceDifference = relativeDifference(existing.distanceMeters, candidate.distanceMeters);
  if (distanceDifference !== null) {
    availableWeight += 0.2;
    if (distanceDifference <= 0.1) {
      score += 0.2;
      reasons.push("distance within 10%");
    }
  }
  if (candidateFile && existing.startTime !== null) {
    const activityDate = new Date(existing.startTime).toISOString().slice(0, 10);
    const dateToken = activityDate.replaceAll("-", "");
    availableWeight += 0.05;
    if (candidateFile.name.includes(dateToken) || candidateFile.name.includes(activityDate)) {
      score += 0.05;
      reasons.push("filename date matches");
    }
    availableWeight += 0.05;
    if (candidateFile.lastModified > 0 && new Date(candidateFile.lastModified).toISOString().slice(0, 10) === activityDate) {
      score += 0.05;
      reasons.push("file metadata date matches");
    }
  }
  const normalizedScore = availableWeight === 0 ? 0 : score / availableWeight;
  return {
    confidence: normalizedScore >= 0.8 && reasons.includes("start time within 10 minutes") ? "high" : normalizedScore >= 0.5 ? "medium" : "low",
    score: Math.round(normalizedScore * 100) / 100,
    reasons
  };
}

export function getActivityCompleteness(session: ActivitySession): ActivityCompleteness {
  const sources = [session.primary, ...session.supportingRideSources, ...session.companionSources].filter((source): source is ActivitySource => source !== null);
  const hasType = (pattern: RegExp) => sources.some(source => pattern.test(source.type));
  const items = [
    { label: "Garmin FIT", present: hasType(/Garmin FIT/) },
    { label: "TrainingPeaks", present: hasType(/TrainingPeaks/) },
    { label: "Tymewear", present: hasType(/Tymewear/) },
    { label: "Nix", present: hasType(/Nix/) },
    { label: "Weather", present: hasType(/Weather/) },
    { label: "Nutrition", present: hasType(/Nutrition/) },
    { label: "Strength", present: hasType(/Workout/) },
    { label: "Athlete Notes", present: hasType(/Athlete Notes/) }
  ];
  const completenessPercent = Math.round(items.filter(item => item.present).length / items.length * 100);
  return { items, completenessPercent };
}

export function applyManualSourceLabel(source: ActivitySource, label: ManualSourceLabel): ActivitySource {
  if (source.role === "companion") {
    const companionTypeByLabel: Partial<Record<ManualSourceLabel, ActivitySourceType>> = {
      Tymewear: "Tymewear CSV",
      Nix: "Nix Biosensor export",
      Weather: "Epic Ride Weather",
      Nutrition: "MacroFactor Nutrition",
      MacroFactor: "MacroFactor Workout",
      Strength: "MacroFactor Workout",
      "Other CSV": "Unknown CSV"
    };
    const type = companionTypeByLabel[label] ?? source.type;
    return {
      ...source,
      type,
      confidence: label === "Tymewear" ? "medium" : "high",
      status: label === "Tymewear" ? "parsed" : "needs confirmation",
      note: label === "Tymewear" ? "Manual Tymewear label · Parsing columns" : `Manual ${label} label`
    };
  }

  const typeByLabel: Record<ManualSourceLabel, ActivitySourceType> = {
    Tymewear: "Tymewear Dashboard Screenshot",
    Nix: "Nix Screenshot",
    CORE: "CORE Screenshot",
    Weather: "Epic Ride Weather",
    Nutrition: "MacroFactor Nutrition",
    MacroFactor: "MacroFactor Workout",
    Strength: "Unknown Screenshot",
    Other: "Unknown Screenshot",
    "Other CSV": "Unknown Screenshot"
  };
  return {
    ...source,
    type: typeByLabel[label],
    confidence: "high",
    status: "needs confirmation",
    note: `${label} screenshot · Values require confirmation`
  };
}

export function recognizeSessionScreenshots(sources: ActivitySource[]): ActivitySource[] {
  const hasTymewearCsv = sources.some(source => source.type === "Tymewear CSV");
  if (!hasTymewearCsv) return sources;
  return sources.map(source => source.role === "screenshot" && source.type === "Unknown Screenshot"
    ? { ...applyManualSourceLabel(source, "Tymewear"), confidence: "medium", note: "Potential Tymewear screenshot · Needs confirmation" }
    : source);
}

export function getRideSourceSummary(session: ActivitySession, options?: { hasPrimaryThermal?: boolean }): RideSourceSummaryItem[] {
  const sources = [session.primary, ...session.supportingRideSources, ...session.companionSources].filter((source): source is ActivitySource => source !== null);
  const hasType = (pattern: RegExp) => sources.some(source => pattern.test(source.type));
  const status = (detected: boolean): RideSourceSummaryItem["status"] => (detected ? "Detected" : "Missing");

  return [
    { label: "Garmin FIT", status: status(hasType(/Garmin FIT/)) },
    { label: "TrainingPeaks", status: status(hasType(/TrainingPeaks/)) },
    { label: "CORE", status: status(Boolean(options?.hasPrimaryThermal) || hasType(/CORE/)) },
    { label: "Tymewear", status: status(hasType(/Tymewear/)) },
    { label: "Apple Health", status: "Not Applicable" },
    { label: "Nix", status: status(hasType(/Nix/)) },
    { label: "Weather", status: status(hasType(/Weather/)) },
    { label: "Nutrition", status: status(hasType(/Nutrition/)) },
    { label: "Athlete Notes", status: status(hasType(/Athlete Notes/)) }
  ];
}